import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/lib/i18n";
import { api, productThumb } from "@/lib/api";
import { ArrowRight, Waves, Droplets, ShieldCheck, Settings, Thermometer, Users } from "lucide-react";
import { HeroVideoPlayer } from "@/components/HeroVideoPlayer";
import SEO from "@/components/SEO";
import { TOP_VIDEO_GALLERY } from "@/lib/heroVideoGallery";

const useInView = (threshold = 0.05) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold, rootMargin: "100px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

export default function HomePage() {
  const { t, tp } = useLanguage();
  const [featured, setFeatured] = useState([]);
  const [catRef, catVisible] = useInView();
  const [engRef, engVisible] = useInView();
  const [wellRef, wellVisible] = useInView();
  const [featRef, featVisible] = useInView();
  const [ctaRef, ctaVisible] = useInView();

  useEffect(() => {
    api.getProducts().then(p => {
      const picks = [
        p.find(x => x.slug === "spa2222"),
        p.find(x => x.slug === "spa2323"),
        p.find(x => x.slug === "jz1818"),
      ].filter(Boolean);
      setFeatured(picks.length ? picks : p.slice(0, 3));
    }).catch(() => {});
  }, []);

  const engineeringFeatures = [
    { icon: Waves, title: t("engineering.acrylic"), desc: tp("American Aristech Acrylic") },
    { icon: ShieldCheck, title: t("engineering.structure"), desc: tp("304 Stainless Steel Frame") },
    { icon: Droplets, title: t("engineering.jets"), desc: tp("Up to 116 Hydro Jets") },
    { icon: Settings, title: t("engineering.filtration"), desc: tp("Ozone Sterilization") },
    { icon: Thermometer, title: t("engineering.control"), desc: tp("Balboa Digital Controls") },
    { icon: Users, title: t("engineering.pumps"), desc: tp("Variable-Speed Technology") },
  ];

  return (
    <div data-testid="home-page">
      <SEO 
        title="Premium Jacuzzi & Spa Systems"
        description="Premium jacuzzi and spa systems engineered for modern living in Dubai & UAE. Rooftop jacuzzi, villa spa, balcony jacuzzi installations with advanced hydrotherapy technology."
        keywords="jacuzzi dubai, spa dubai, rooftop jacuzzi, villa jacuzzi, balcony jacuzzi, hot tub dubai, hydrotherapy spa, luxury spa UAE, swimming pool, pool, plunge pool, hot tub, fiberglass pool, jacuzzi tub, swimming pool contractors, lap pool, pool contractors, swimming pool construction, aqua pools, indoor pool, portable swimming pool, indoor swimming pool, infinity swimming pool"
        canonical={typeof window !== 'undefined' ? window.location.origin : "https://www.pruvajacuzzi.com"}
        videos={TOP_VIDEO_GALLERY.slice(0, 3).map((video, index) => ({
          url: video.url,
          name: `Pruva Jacuzzi Showcase ${index + 1}`,
          description: "Premium jacuzzi and spa installations by Pruva - luxury hydrotherapy systems for villas, rooftops, and balconies in Dubai, UAE.",
          thumbnailUrl: "/images/optimized/og-image.webp",
          uploadDate: "2024-12-01",
          duration: "PT30S"
        }))}
      />
      {/* Hero */}
      <section data-testid="hero-section" className="relative h-[75vh] md:h-screen flex items-end">
        {/* Hero Video Player */}
        <HeroVideoPlayer />
        
        <div className="relative z-10 container mx-auto px-4 md:px-8 max-w-7xl pb-20 md:pb-28">
          <p className="text-sm uppercase tracking-[0.3em] text-slate-300 mb-4 animate-fade-in-up opacity-0 animation-delay-100" style={{ fontFamily: "'Inter', sans-serif" }}>
            Pruva Jacuzzi
          </p>
          <h1 data-testid="hero-title" className="text-4xl sm:text-5xl lg:text-7xl font-semibold text-white leading-tight tracking-tight max-w-4xl animate-fade-in-up opacity-0 animation-delay-200">
            {t("hero.title")}
          </h1>
          <p className="text-lg md:text-xl text-slate-200/80 mt-6 max-w-2xl leading-relaxed animate-fade-in-up opacity-0 animation-delay-300" style={{ fontFamily: "'Inter', sans-serif" }}>
            {t("hero.subtitle")}
          </p>
          <div className="flex flex-wrap gap-4 mt-10 animate-fade-in-up opacity-0 animation-delay-400">
            <Link to="/spa-series" data-testid="hero-cta-catalog"
              className="bg-white text-slate-800 px-8 py-3.5 text-sm uppercase tracking-widest font-medium hover:bg-slate-100 transition-colors duration-300">
              {t("hero.ctaCatalog")}
            </Link>
            <Link to="/contact" data-testid="hero-cta-contact"
              className="border border-white/50 text-white px-8 py-3.5 text-sm uppercase tracking-widest font-medium hover:bg-white/10 transition-colors duration-300">
              {t("hero.ctaContact")}
            </Link>
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section ref={catRef} className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className={`transition-all duration-700 ${catVisible ? 'opacity-100 translate-y-0' : 'opacity-80 translate-y-4'}`}>
            <p className="text-sm uppercase tracking-widest text-amber-700 font-medium mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
              {t("home.categoriesTitle")}
            </p>
            <h2 data-testid="categories-title" className="text-4xl md:text-5xl font-medium tracking-tight text-slate-900 mb-16">
              {t("home.categoriesSubtitle")}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { to: "/spa-series", title: t("products.spaSeries"), desc: t("products.spaDesc"), img: "/images/optimized/spa-category.webp" },
              { to: "/jacuzzi-series", title: t("products.jacuzziSeries"), desc: t("products.jacuzziDesc"), img: "/images/optimized/jacuzzi-category.webp" },
            ].map((cat, i) => (
              <Link key={cat.to} to={cat.to} data-testid={`category-${i === 0 ? 'spa' : 'jacuzzi'}`}
                className={`group relative overflow-hidden aspect-[4/3] transition-all duration-700 ${catVisible ? 'opacity-100 translate-y-0' : 'opacity-80 translate-y-4'}`}
                style={{ transitionDelay: `${200 + i * 150}ms` }}>
                <img src={cat.img} alt={cat.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 50%)" }} />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl md:text-3xl font-medium text-white mb-2">{cat.title}</h3>
                  <p className="text-slate-200/80 text-sm leading-relaxed max-w-md" style={{ fontFamily: "'Inter', sans-serif" }}>{cat.desc}</p>
                  <span className="inline-flex items-center gap-2 text-sm text-white mt-4 uppercase tracking-widest font-medium group-hover:gap-3 transition-all duration-300">
                    {t("products.viewAll")} <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Engineering */}
      <section ref={engRef} className="py-16 md:py-20" style={{ background: "linear-gradient(135deg, #F8F9FA 0%, #F1F3F5 100%)" }}>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className={`transition-all duration-700 ${engVisible ? 'opacity-100 translate-y-0' : 'opacity-80 translate-y-4'}`}>
            <p className="text-sm uppercase tracking-widest text-amber-700 font-medium mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
              {t("home.engineeringTitle")}
            </p>
            <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-slate-900 mb-16">
              {t("home.engineeringSubtitle")}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {engineeringFeatures.map((feat, i) => (
              <div key={i} data-testid={`eng-feature-${i}`}
                className={`p-8 bg-white border border-slate-100 hover:border-slate-300 transition-all duration-500 ${engVisible ? 'opacity-100 translate-y-0' : 'opacity-80 translate-y-4'}`}
                style={{ transitionDelay: `${200 + i * 100}ms` }}>
                <feat.icon className="w-8 h-8 text-slate-800 mb-5" strokeWidth={1.5} />
                <h3 className="text-lg font-medium text-slate-900 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>{feat.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wellness */}
      <section ref={wellRef} className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center transition-all duration-700 ${wellVisible ? 'opacity-100 translate-y-0' : 'opacity-80 translate-y-4'}`}>
            <div>
              <p className="text-sm uppercase tracking-widest text-amber-700 font-medium mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
                {t("home.wellnessTitle")}
              </p>
              <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-slate-900 mb-8">
                {t("home.wellnessSubtitle")}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8" style={{ fontFamily: "'Inter', sans-serif" }}>
                {t("home.wellnessText")}
              </p>
              <Link to="/engineering" data-testid="wellness-cta"
                className="inline-flex items-center gap-2 bg-slate-800 text-white px-8 py-3 text-sm uppercase tracking-widest font-medium hover:bg-slate-700 transition-colors duration-300">
                {t("nav.engineering")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="relative aspect-[4/3] overflow-hidden">
              <img src="/images/optimized/wellness.webp" alt="Wellness lifestyle" loading="lazy"
                className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Models */}
      {featured.length > 0 && (
        <section ref={featRef} className="py-16 md:py-24 bg-slate-50">
          <div className="container mx-auto px-4 md:px-8 max-w-7xl">
            <div className={`transition-all duration-700 ${featVisible ? 'opacity-100 translate-y-0' : 'opacity-80 translate-y-4'}`}>
              <p className="text-sm uppercase tracking-widest text-amber-700 font-medium mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
                {t("home.featuredTitle")}
              </p>
              <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-slate-900 mb-16">
                {t("home.featuredSubtitle")}
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {featured.map((p, i) => (
                <Link key={p.slug} to={`/${p.series === 'spa' ? 'spa-series' : 'jacuzzi-series'}/${p.slug}`}
                  data-testid={`featured-${p.slug}`}
                  className={`group bg-white overflow-hidden hover:shadow-xl transition-all duration-500 ${featVisible ? 'opacity-100 translate-y-0' : 'opacity-80 translate-y-4'}`}
                  style={{ transitionDelay: `${200 + i * 150}ms` }}>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={productThumb(p)} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = p.image_url; }} loading="lazy" alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" width="400" height="400" decoding="async" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xl font-medium text-slate-900" style={{ fontFamily: "'Inter', sans-serif" }}>{p.name}</h3>
                      <span className="text-xs uppercase tracking-wider text-slate-600 bg-slate-100 px-2 py-1 font-medium">{p.series === 'spa' ? t("products.spaSeries") : t("products.jacuzziSeries")}</span>
                    </div>
                    <p className="text-sm text-slate-500 mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>{tp(p.subtitle)}</p>
                    <div className="flex items-center gap-4 text-xs text-slate-400" style={{ fontFamily: "'Inter', sans-serif" }}>
                      <span>{p.capacity} {t("products.persons")}</span>
                      <span>{p.hydro_jets} {t("products.hydroJets")}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Developer Solutions */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative aspect-[4/3] overflow-hidden order-2 lg:order-1">
              <img src="/images/optimized/developer.webp" alt="Developer solutions" loading="lazy"
                className="w-full h-full object-cover" />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-sm uppercase tracking-widest text-amber-700 font-medium mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
                {t("home.developerTitle")}
              </p>
              <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-slate-900 mb-6">
                {t("home.turnkeyTitle")}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
                {t("home.developerText")}
              </p>
              <p className="text-base text-slate-500 leading-relaxed mb-8" style={{ fontFamily: "'Inter', sans-serif" }}>
                {t("home.turnkeyDesc")}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/custom-solutions/developer-services" data-testid="developer-cta"
                  className="inline-flex items-center gap-2 bg-slate-800 text-white px-8 py-3 text-sm uppercase tracking-widest font-medium hover:bg-slate-700 transition-colors duration-300">
                  {t("home.turnkeyServices")} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/custom-solutions"
                  className="inline-flex items-center gap-2 border border-slate-800 text-slate-800 px-8 py-3 text-sm uppercase tracking-widest font-medium hover:bg-slate-100 transition-colors duration-300">
                  {t("nav.customSolutions")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Civil Defense Approved Installation Banner */}
      <section className="py-12 md:py-16 bg-gradient-to-r from-emerald-900 via-emerald-800 to-emerald-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        </div>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            <div className="flex-shrink-0">
              <div className="w-20 h-20 md:w-24 md:h-24 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white/30">
                <svg className="w-10 h-10 md:w-12 md:h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
            </div>
            <div className="flex-1 text-center lg:text-start">
              <span className="inline-block px-3 py-1 bg-white/20 text-white text-xs uppercase tracking-widest font-medium rounded-full mb-3">
                {t("home.civilDefenseBadge")}
              </span>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-white mb-3">
                {t("home.civilDefenseTitle")}
              </h2>
              <p className="text-emerald-100 text-base md:text-lg max-w-3xl">
                {t("home.civilDefenseText")}
              </p>
            </div>
            <div className="flex-shrink-0 hidden xl:block">
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2 text-white/90 text-sm">
                  <svg className="w-5 h-5 text-emerald-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t("home.civilDefenseFeature1")}
                </div>
                <div className="flex items-center gap-2 text-white/90 text-sm">
                  <svg className="w-5 h-5 text-emerald-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t("home.civilDefenseFeature2")}
                </div>
                <div className="flex items-center gap-2 text-white/90 text-sm">
                  <svg className="w-5 h-5 text-emerald-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t("home.civilDefenseFeature3")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section ref={ctaRef} className="py-16 md:py-24 bg-slate-50 border-t border-slate-100">
        <div className={`container mx-auto px-4 md:px-8 max-w-7xl text-center transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-80 translate-y-4'}`}>
          <h2 data-testid="cta-title" className="text-4xl md:text-5xl font-medium text-slate-900 tracking-tight mb-6">{t("home.ctaTitle")}</h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto mb-10" style={{ fontFamily: "'Inter', sans-serif" }}>{t("home.ctaSubtitle")}</p>
          <Link to="/contact" data-testid="cta-button"
            className="inline-block bg-slate-800 text-white px-10 py-4 text-sm uppercase tracking-widest font-medium hover:bg-slate-700 transition-colors duration-300">
            {t("home.ctaButton")}
          </Link>
        </div>
      </section>
    </div>
  );
}
